// "Rentabilité" — ad spend vs confirmed albums (2026-07-25).
//
// Headline Yahya asked for: what ONE ALBUM cost in ads (spend ÷ albums
// confirmed), plus the true bottom line (profit − spend) for the same window.
//
// 2026-10-21: the profit here is the worker's ONE shared formula (lib/profit.ts)
// — orders DELIVERED in the window, all ad campaigns, commissions and expenses
// — so it is the same number as the home's « Profit net » and the accounting
// page. When the ad data is missing the profit shows « — », never a fake 0.
//
// Admin-only: the endpoint is gated on `marketing.manage`, and the card is
// only rendered for users holding it — it exposes both ad spend AND margin.
// Loads independently of the rest of the dashboard and never estimates: a
// failed load shows "indisponible", never a guessed number.
import { useQuery } from '@tanstack/react-query';
import { marketingApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

const mad = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} MAD`;

export function ProfitabilityCard({ range }: { range?: { from?: string; to?: string } }) {
  const { t } = useT();
  const { data, isLoading, error } = useQuery({
    // Re-fetches automatically when the dashboard's date range changes.
    queryKey: ['profitability', range?.from ?? null, range?.to ?? null],
    queryFn: () => marketingApi.profitability({ from: range?.from, to: range?.to }),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const p = data?.profitability;
  const profitable = (p?.net_profit_mad ?? 0) >= 0;
  const adsMissing = !!p?.ads_missing;

  return (
    <div
      className="mb-7 bg-white rounded-xl border overflow-hidden animate-[fadeInUp_0.5s_ease-out_both]"
      style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}
    >
      {error && (
        <div className="px-5 py-4">
          <p className="text-sm font-medium" style={{ color: 'var(--color-status-stuck)' }}>
            {t('Données publicitaires indisponibles')}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('Aucun chiffre affiché plutôt qu’un chiffre faux. Réessayez plus tard.')}
          </p>
        </div>
      )}

      {!error && (
        <>
          {/* Headline: cost of one album + the bottom line. */}
          <div className="px-5 py-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1">
                {t('Coût par album')}
              </p>
              <p className="text-[26px] leading-none" style={{ color: 'var(--color-adm-ink)', fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}>
                {isLoading ? '…' : p?.cost_per_album == null ? '—' : mad(p.cost_per_album)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5">
                {isLoading || !p ? '' : adsMissing ? t('données pub manquantes') : t('{s} de pub ÷ {n} albums', { s: mad(p.spend_mad), n: p.confirmed_albums })}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1">
                {t('Profit net')}
              </p>
              <p
                className="text-[26px] leading-none"
                style={{ color: isLoading ? 'var(--color-adm-ink)' : profitable ? 'var(--color-status-done)' : 'var(--color-status-stuck)', fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}
              >
                {isLoading ? '…' : p == null || p.net_profit_mad == null ? '—' : mad(p.net_profit_mad)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5">
                {isLoading || !p ? '' : adsMissing
                  ? t('données pub manquantes (dernière synchro {d}) — {x} avant pub', { d: p.ads_last_sync ?? '—', x: mad(p.profit_before_ads_mad ?? 0) })
                  : t('{p} de marge − {s} de pub − {c} de commissions − {e} de charges', {
                    p: mad(p.profit_mad), s: mad(p.spend_mad), c: mad(p.commissions_mad ?? 0), e: mad(p.expenses_mad ?? 0),
                  })}
              </p>
            </div>
          </div>

          {/* Supporting detail. */}
          {p && (
            <div className="px-5 pb-4 flex flex-wrap gap-2 text-[11px]">
              <span className="px-2.5 py-1 rounded-full text-[#6B5423] dark:text-adm-gold-ink" style={{ background: 'rgba(201,162,74,0.10)' }}>
                {t('{n} commandes confirmées', { n: p.confirmed_orders })}
              </span>
              <span className="px-2.5 py-1 rounded-full text-[#6B5423] dark:text-adm-gold-ink" style={{ background: 'rgba(201,162,74,0.10)' }}>
                {t('{n} albums', { n: p.confirmed_albums })}
              </span>
              {p.commissions_mad > 0 && (
                <span className="px-2.5 py-1 rounded-full" style={{ background: 'var(--color-adm-surface-3)', color: 'var(--color-adm-muted)' }}>
                  {t('Commissions agents')} {mad(p.commissions_mad)}
                </span>
              )}
              {p.cost_per_order != null && (
                <span className="px-2.5 py-1 rounded-full" style={{ background: 'var(--color-adm-surface-3)', color: 'var(--color-adm-muted)' }}>
                  {t('Coût par commande')} {mad(p.cost_per_order)}
                </span>
              )}
              {p.spend_excluded_mad > 0 && (
                <span
                  className="px-2.5 py-1 rounded-full bg-[#FEF9C3] dark:bg-status-waiting-bg text-[#854D0E] dark:text-status-waiting"
                  title={p.excluded_campaigns.join(' · ')}
                >
                  {t('Notoriété exclue')}: {mad(p.spend_excluded_mad)}
                </span>
              )}
            </div>
          )}

          {/* Honest caveat — spend and profit sit in the same window but answer
              different questions (today's spend buys orders that deliver
              later), so short ranges look worse than reality. */}
          <div className="px-5 py-2.5 border-t bg-[#FAFAF8] dark:bg-adm-surface-2" style={{ borderColor: 'rgba(201,162,74,0.12)' }}>
            <p className="text-[10.5px] text-gray-500">
              {t('La pub dépensée aujourd’hui produit des commandes livrées plus tard — sur une courte période le profit paraît plus bas qu’il ne l’est.')}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
